import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Phone, Mail, MapPin, Building, User, Plus, Edit, Trash2 } from 'lucide-react';
import '../components/styles.css';

export default function Clients() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    client_type: 'particulier',
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    siret: '',
    notes: '',
    status: 'actif'
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        setUser(currentUser);
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        navigate('/crm/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterType, filterStatus]);

  const fetchClients = async () => {
    try {
      const supabase = createClient();
      
      let query = supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterType !== 'all') {
        query = query.eq('client_type', filterType);
      }

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erreur lors de la récupération des clients:', error);
        alert('Erreur lors de la récupération des clients: ' + error.message);
      } else {
        setClients(data || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error);
      alert('Erreur lors de la récupération des clients: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supabase = createClient();

    // Validation: if entreprise, require company_name
    if (formData.client_type === 'entreprise' && !formData.company_name) {
      alert('Le nom de l\'entreprise est requis pour les entreprises');
      return;
    }

    // Validation: last_name and phone are required
    if (!formData.last_name || !formData.last_name.trim()) {
      alert('Le nom est requis');
      return;
    }

    if (!formData.phone || !formData.phone.trim()) {
      alert('Le téléphone est requis');
      return;
    }

    // Clean data: convert empty strings to null for optional fields
    const cleanedData = {
      client_type: formData.client_type || 'particulier',
      last_name: formData.last_name.trim(),
      phone: formData.phone.trim(),
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      notes: formData.notes?.trim() || null,
      status: formData.status || 'actif'
    };

    // Include optional columns
    if (formData.first_name?.trim()) {
      cleanedData.first_name = formData.first_name.trim();
    }
    if (formData.client_type === 'entreprise' && formData.company_name?.trim()) {
      cleanedData.company_name = formData.company_name.trim();
    }
    if (formData.client_type === 'entreprise' && formData.siret?.trim()) {
      cleanedData.siret = formData.siret.trim();
    }

    if (editingClient) {
      // Update existing client
      const { error } = await supabase
        .from('clients')
        .update(cleanedData)
        .eq('id', editingClient.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour du client: ' + (error.message || JSON.stringify(error)));
      } else {
        setShowForm(false);
        setEditingClient(null);
        resetForm();
        fetchClients();
      }
    } else {
      // Create new client
      const { error } = await supabase
        .from('clients')
        .insert([cleanedData]);

      if (error) {
        console.error('Erreur lors de la création:', error);
        alert('Erreur lors de la création du client: ' + (error.message || JSON.stringify(error)));
      } else {
        setShowForm(false);
        resetForm();
        fetchClients();
      }
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      client_type: client.client_type || 'particulier',
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      company_name: client.company_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      siret: client.siret || '',
      notes: client.notes || '',
      status: client.status || 'actif'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.')) {
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du client');
      } else {
        fetchClients();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du client');
    }
  };

  const resetForm = () => {
    setFormData({
      client_type: 'particulier',
      first_name: '',
      last_name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      siret: '',
      notes: '',
      status: 'actif'
    });
    setEditingClient(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'actif': return 'bg-green-100 text-green-800';
      case 'inactif': return 'bg-yellow-100 text-yellow-800';
      case 'archive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Chargement des clients...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
            <p className="mt-2 text-gray-600">Gérez votre base de clients</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Ajouter un client
          </button>
        </div>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Tous les types</option>
            <option value="particulier">Particuliers</option>
            <option value="entreprise">Entreprises</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Tous les statuts</option>
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
            <option value="archive">Archivé</option>
          </select>
        </div>

        {showForm && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingClient ? 'Modifier le client' : 'Nouveau client'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de client *
                </label>
                <select
                  required
                  value={formData.client_type}
                  onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="particulier">Particulier</option>
                  <option value="entreprise">Entreprise</option>
                </select>
              </div>

              {formData.client_type === 'entreprise' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.client_type === 'particulier' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.client_type === 'entreprise' ? 'Contact principal' : 'Nom'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                {formData.client_type === 'entreprise' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Numéro SIRET
                    </label>
                    <input
                      type="text"
                      value={formData.siret}
                      onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                      placeholder="Ex: 12345678901234"
                      maxLength="14"
                      className="w-full p-2 border rounded-md"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      SIRET à 14 chiffres pour la facturation
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="archive">Archivé</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingClient ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {clients.length === 0 ? (
              <li className="p-6 text-center text-gray-500">Aucun client trouvé.</li>
            ) : (
              clients.map((client) => (
                <li key={client.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {client.client_type === 'entreprise' ? (
                          <Building className="h-5 w-5 text-primary-600" />
                        ) : (
                          <User className="h-5 w-5 text-primary-600" />
                        )}
                        <span className="text-lg font-semibold text-gray-900">
                          {client.client_type === 'entreprise' 
                            ? client.company_name 
                            : `${client.first_name || ''} ${client.last_name}`.trim()}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                          {client.status === 'actif' && 'Actif'}
                          {client.status === 'inactif' && 'Inactif'}
                          {client.status === 'archive' && 'Archivé'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {client.client_type === 'entreprise' ? 'Entreprise' : 'Particulier'}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        {client.client_type === 'entreprise' && client.last_name && (
                          <p>Contact: <span className="font-medium">{client.last_name}</span></p>
                        )}
                        {client.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${client.phone}`} className="text-primary-600 hover:underline">
                              {client.phone}
                            </a>
                          </p>
                        )}
                        {client.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${client.email}`} className="text-primary-600 hover:underline">
                              {client.email}
                            </a>
                          </p>
                        )}
                        {client.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {client.address}
                          </p>
                        )}
                        {client.client_type === 'entreprise' && client.siret && (
                          <p className="font-medium text-primary-600">
                            SIRET: {client.siret}
                          </p>
                        )}
                        {client.notes && (
                          <p className="text-gray-700 mt-2">{client.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end space-y-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(client.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(client)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          <Edit className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}

